import { useId, useState } from 'react';

import { Badge, Button, Card, Icon, IconButton } from '@ds/core';
import { Alert } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { CORES_DA_ETAPA, OPCOES_DE_COR, etapasAtivas, moverEtapa, problemaDaEdicao } from '@/data/funis';
import type { EtapaEditada } from '@/data/funis';
import { dados } from '@/data/porta';
import type { CorDaEtapa, Funil } from '@/data/tipos';
import { ArquivarEtapaModal } from './ArquivarEtapaModal';

export interface EditorDeEtapasProps {
    funil: Funil;
    /** Leads por id de etapa, no recorte que a tela carregou. */
    leadsPorEtapa: Record<string, number>;
    /** A busca ou o responsável estão filtrando: a contagem não é a do pipeline inteiro. */
    filtrado: boolean;
    podeEditar: boolean;
    onMudou: () => void;
}

function paraEdicao(funil: Funil): EtapaEditada[] {
    return etapasAtivas(funil).map((e) => ({ id: e.id, titulo: e.titulo, cor: e.cor, tipo: e.tipo }));
}

/**
 * As etapas de um pipeline — trilha T7.
 *
 * Renomear, trocar a cor, reordenar e criar são uma gravação só ("Salvar
 * pipeline"). Arquivar é à parte, porque tem regra: com leads na etapa, é preciso
 * dizer para onde eles vão. As etapas finais (ganho e perda) se renomeiam, mas não
 * sobem, não descem e não se arquivam.
 */
export function EditorDeEtapas({ funil, leadsPorEtapa, filtrado, podeEditar, onMudou }: EditorDeEtapasProps) {
    const aviso = useAviso();
    const idNome = useId();
    const [nome, setNome] = useState(funil.nome);
    const [etapas, setEtapas] = useState<EtapaEditada[]>(() => paraEdicao(funil));
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);
    const [arquivando, setArquivando] = useState<{ id: string; titulo: string } | null>(null);

    const sujo = JSON.stringify({ nome, etapas }) !== JSON.stringify({ nome: funil.nome, etapas: paraEdicao(funil) });

    const alterar = (indice: number, mudanca: Partial<EtapaEditada>) => {
        setErro(null);
        setEtapas((lista) => lista.map((e, i) => (i === indice ? { ...e, ...mudanca } : e)));
    };

    const adicionar = () => {
        setErro(null);
        setEtapas((lista) => {
            /* A nova entra depois das abertas e antes das finais. */
            const primeiraFinal = lista.findIndex((e) => e.tipo !== 'aberta');
            const posicao = primeiraFinal === -1 ? lista.length : primeiraFinal;
            let n = 1;
            while (lista.some((e) => e.titulo.trim().toLocaleLowerCase('pt-BR') === `nova etapa ${n}`)) n += 1;
            const nova: EtapaEditada = { titulo: `Nova etapa ${n}`, cor: 'neutro', tipo: 'aberta' };
            return [...lista.slice(0, posicao), nova, ...lista.slice(posicao)];
        });
    };

    const salvar = async () => {
        const problema = nome.trim() ? problemaDaEdicao(etapas) : 'Dê um nome ao pipeline.';
        if (problema) {
            setErro(problema);
            return;
        }
        setSalvando(true);
        try {
            await dados().salvarFunil(funil.id, { nome, etapas });
            aviso({ title: 'Pipeline salvo', description: `${nome.trim()} · ${etapas.length} etapas` });
            onMudou();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar o pipeline.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Card title="Etapas do pipeline" subtitle="A ordem define o que é avançar" padding={18}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {podeEditar && (
                    <FormField label="Nome do pipeline" htmlFor={idNome} required>
                        <Input id={idNome} value={nome} onChange={(v) => { setErro(null); setNome(v); }} />
                    </FormField>
                )}

                {etapas.map((e, i) => {
                    const final = e.tipo !== 'aberta';
                    const quantos = e.id ? (leadsPorEtapa[e.id] ?? 0) : 0;
                    const posicao = i + 1;
                    return (
                        <div
                            key={e.id ?? `nova-${i}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}
                        >
                            <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: '50%', background: CORES_DA_ETAPA[e.cor], flex: '0 0 auto' }} />
                            {podeEditar ? (
                                <>
                                    <div style={{ flex: '1 1 170px', minWidth: 0 }}>
                                        <Input value={e.titulo} onChange={(v) => alterar(i, { titulo: v })} aria-label={`Título da etapa ${posicao}`} />
                                    </div>
                                    <div style={{ width: 130 }}>
                                        <Select
                                            value={e.cor} onChange={(v) => alterar(i, { cor: v as CorDaEtapa })} options={OPCOES_DE_COR} size="sm"
                                            aria-label={`Cor da etapa ${posicao}`}
                                        />
                                    </div>
                                </>
                            ) : (
                                <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600 }}>{posicao}. {e.titulo}</span>
                            )}
                            {final && <Badge tone={e.tipo === 'ganho' ? 'success' : 'neutral'}>{e.tipo === 'ganho' ? 'Final: ganho' : 'Final: perda'}</Badge>}
                            {!e.id && <Badge tone="info">Nova</Badge>}
                            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                {quantos} {quantos === 1 ? 'lead' : 'leads'}
                            </span>
                            {podeEditar && !final && (
                                <span style={{ display: 'inline-flex', gap: 4, marginLeft: 'auto' }}>
                                    <IconButton
                                        variant="ghost" icon={<Icon name="arrow-up" size={14} />} label={`Subir ${e.titulo}`}
                                        onClick={() => setEtapas((lista) => moverEtapa(lista, i, -1))} disabled={i === 0}
                                    />
                                    <IconButton
                                        variant="ghost" icon={<Icon name="arrow-down" size={14} />} label={`Descer ${e.titulo}`}
                                        onClick={() => setEtapas((lista) => moverEtapa(lista, i, 1))} disabled={etapas[i + 1]?.tipo !== 'aberta'}
                                    />
                                    {e.id ? (
                                        /* Arquivar grava na hora e recarrega o funil: com
                                           alteração não salva, ela se perderia. */
                                        <Button
                                            size="sm" variant="ghost" disabled={sujo}
                                            title={sujo ? 'Salve as alterações antes de arquivar' : undefined}
                                            onClick={() => setArquivando({ id: e.id as string, titulo: e.titulo })}
                                        >
                                            Arquivar
                                        </Button>
                                    ) : (
                                        <Button size="sm" variant="ghost" onClick={() => setEtapas((lista) => lista.filter((_, k) => k !== i))}>
                                            Descartar
                                        </Button>
                                    )}
                                </span>
                            )}
                        </div>
                    );
                })}

                {podeEditar && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button variant="secondary" iconLeft={<Icon name="plus" size={14} />} onClick={adicionar}>Adicionar etapa</Button>
                        <span style={{ marginLeft: 'auto' }} />
                        {sujo && (
                            <Button variant="ghost" onClick={() => { setErro(null); setNome(funil.nome); setEtapas(paraEdicao(funil)); }}>
                                Descartar alterações
                            </Button>
                        )}
                        <Button onClick={() => void salvar()} loading={salvando} disabled={!sujo}>Salvar pipeline</Button>
                    </div>
                )}

                {/* Perdido não é coluna do quadro: ele sai do quadro, mas continua na
                    lista e nos relatórios — motivo de perda é o dado mais útil do CRM
                    depois da venda. */}
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    Lead perdido não ocupa coluna no quadro, e continua visível na lista.
                    {filtrado && ' A contagem de leads segue a busca e o responsável escolhidos no topo.'}
                </p>
                {!podeEditar && (
                    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                        Renomear, reordenar e criar etapas exige a permissão de configurações da clínica.
                    </p>
                )}
            </div>

            <ArquivarEtapaModal
                funil={funil}
                etapa={arquivando}
                quantidade={arquivando ? (leadsPorEtapa[arquivando.id] ?? 0) : 0}
                onClose={() => setArquivando(null)}
                onArquivada={onMudou}
            />
        </Card>
    );
}
